<template>
    <!-- Shipping is a deferred prop; reserve the height so the buy box does not shift. -->
    <div v-if="isBlockVisible && isAwaitingShipping" class="mt-3" aria-hidden="true">
        <div class="min-h-12 w-full animate-pulse rounded-md bg-gray-100/50 lg:border lg:border-border/40" />
    </div>

    <div v-else-if="isBlockVisible && deliveryLabel" class="mt-3">
        <div class="relative flex min-h-12 w-full items-center gap-4 rounded-md bg-gray-100/50 px-3 py-1 lg:border lg:border-border/40">
            <div class="flex items-center gap-4">
                <TruckIcon aria-hidden="true" />
                <span class="flex flex-col">
                    <span class="block" v-safe-html="$t(deliveryLabel.key, deliveryLabel.params, deliveryLabel.plural ?? 1)" />
                    <span v-if="urgentDeliveryDescription" class="text-xs italic" v-safe-html="urgentDeliveryDescription" />
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import TruckIcon from '@/icons/shipping/TruckIcon.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import type { MagentoPaazlShippingOptionObject } from '@/types/generated';
import _ from 'lodash';
import { computed } from 'vue';

type DeliveryLabel = {
    key: string;
    params: Record<string, number>;
    plural?: number;
};

const productPageStore = useProductPageStore();

const shippingOptions = computed<MagentoPaazlShippingOptionObject[]>(() => productPageStore.shipping?.shipping_options ?? []);

const hasTomorrowShipping = computed<boolean>(() => _.some(shippingOptions.value, 'is_tomorrow_delivery'));
const hasDayAfterTomorrowShipping = computed<boolean>(() => _.some(shippingOptions.value, 'is_after_day_after_tomorrow'));

/** Anything but the new method is the original one; the server hides the block when there is neither. */
const isNewMethod = computed<boolean>(() => productPageStore.shipping?.is_new_delivery_method ?? false);

/** Decided upfront from date-independent data, so the block never appears or disappears after load. */
const isBlockVisible = computed<boolean>(() => productPageStore.showDeliveryInformation ?? false);

const isAwaitingShipping = computed<boolean>(() => productPageStore.shipping === undefined);

/** Wording for the fastest option when the method's own rules have nothing to say. */
const fastestDeliveryLabel = computed((): DeliveryLabel => {
    const days = productPageStore.shipping?.days_until_delivery ?? 0;

    if (days <= 0) {
        return { key: 'general.delivery_options.today_delivery.label', params: {} };
    }

    if (days === 1) {
        return { key: 'general.delivery_options.tomorrow_delivery.label', params: {} };
    }

    if (days === 2) {
        return { key: 'general.delivery_options.day_after_tomorrow.label', params: {} };
    }

    if (!isNewMethod.value) {
        return { key: 'general.delivery_options.delivered_in_days.label', params: { days } };
    }

    const businessDays = productPageStore.shipping?.business_days_until_delivery ?? days;

    return { key: 'general.delivery_options.delivered_in_business_days.label', params: { days: businessDays }, plural: businessDays };
});

const tomorrowOrDayAfterLabel = computed((): DeliveryLabel => ({
    key: hasTomorrowShipping.value ? 'general.delivery_options.tomorrow_delivery.label' : 'general.delivery_options.day_after_tomorrow.label',
    params: {},
}));

const deliveryLabel = computed((): DeliveryLabel | undefined => {
    if (shippingOptions.value.length === 0) {
        return undefined;
    }

    if (hasTomorrowShipping.value || hasDayAfterTomorrowShipping.value) {
        return tomorrowOrDayAfterLabel.value;
    }

    return fastestDeliveryLabel.value;
});

const urgentDeliveryDescription = computed<string | undefined>(() => {
    const description = productPageStore.configurableProduct.urgent_delivery_description;

    if (!description) {
        return undefined;
    }

    if (!isNewMethod.value && !hasTomorrowShipping.value) {
        return undefined;
    }

    return description;
});
</script>
